import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { PPWR_PLAN_LIMITS } from '../../config/plans';
import { IoCheckmarkCircle, IoAlertCircleOutline, IoCopyOutline, IoArchiveOutline, IoArrowUndoOutline } from 'react-icons/io5';
import CustomModal from '../../hoc/customModal';
import CustomModalFullscreen from '../../hoc/customModalFullscreen';
import { Loader } from '../../utils/tools';
import { fetchPackagingUnits, deletePackagingUnit, setPackagingUnitArchived } from '../../store/actions/packagingUnit';
import { PACKAGING_TYPE_LABELS, PACKAGING_TYPE_OPTIONS } from '../../utils/constants/packagingTypes';
import { PACKAGING_CHARACTER_LABELS, PACKAGING_CHARACTER_OPTIONS } from '../../utils/constants/packagingCharacters';
import { getRoleBadges, ROLE_FILTER_OPTIONS, matchesRoleFilter, getRequiredDocumentCategoryKeys } from '../../utils/constants/packagingRoles';
import { ReactComponent as RemoveIcon } from '../../assets/remove.svg';
import { ReactComponent as DocumentIcon } from '../../assets/document.svg';
import { ReactComponent as EditIcon } from '../../assets/edit.svg';
import { ReactComponent as BtnBack } from '../../assets/btn-back.svg';
import AddPackagingUnit from './packagingUnit/add';
import RoleWizard from './roleWizard';
import PackagingUnitDetail from './packagingUnit/detail';
import EditPackagingUnit from './packagingUnit/edit';
import DocumentationStatusChart from './charts/DocumentationStatusChart';
import RoleDistributionChart from './charts/RoleDistributionChart';

const PAGE_SIZE = 10;

export const ModuleGate = () => {
    const navigate = useNavigate();
    return (
        <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            minHeight: '40vh', textAlign: 'center', padding: '40px 20px',
        }}>
            <div style={{
                width: 64, height: 64, borderRadius: '50%', backgroundColor: '#F4F5FE',
                display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24,
            }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0B1157" strokeWidth="1.8">
                    <rect x="3" y="11" width="18" height="11" rx="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
            </div>
            <h5 style={{ color: '#0B1157', fontWeight: 700, marginBottom: 8 }}>PPWR modul nie je aktivovaný</h5>
            <p style={{ color: '#718094', fontSize: 14, maxWidth: 360, marginBottom: 24, lineHeight: 1.6 }}>
                Modul PPWR (správa obalových jednotiek) nie je súčasťou vášho predplatného.
                Aktivujte demo zadarmo alebo si vyberte platený plán.
            </p>
            <Button className="btn-custom-small px-4" onClick={() => navigate('/billing')}>
                Aktivovať PPWR modul
            </Button>
        </div>
    );
};

const UnitLimitBanner = ({ current, max }) => {
    const atLimit = current >= max;
    return (
        <div style={{
            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
            backgroundColor: atLimit ? '#F7E4DE' : '#FFF8E7',
            border: `1px solid ${atLimit ? '#A5432B' : '#C97D2C'}`,
            borderRadius: 10, padding: '14px 16px', marginBottom: 16,
        }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={atLimit ? '#A5432B' : '#C97D2C'} strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}>
                    <path d="M12 9v4M12 17h.01M10.3 3.9L2.4 18a2 2 0 0 0 1.7 3h16a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
                </svg>
                <div>
                    <p style={{ fontSize: 13.5, fontWeight: 600, color: atLimit ? '#A5432B' : '#C97D2C', margin: '0 0 2px' }}>
                        {atLimit ? 'Dosiahli ste limit obalových jednotiek' : `Limit obalových jednotiek: ${current} / ${max}`}
                    </p>
                    <div style={{ fontSize: 12.5, color: '#3D5654', lineHeight: 1.5 }}>
                        {atLimit
                            ? 'Váš plán PPWR neumožňuje pridať ďalšiu obalovú jednotku.'
                            : `Váš plán PPWR umožňuje maximálne ${max} ${max === 1 ? 'obalovú jednotku' : max < 5 ? 'obalové jednotky' : 'obalových jednotiek'}.`
                        }
                    </div>
                </div>
            </div>
            {atLimit && (
                <Link to="/billing" style={{
                    whiteSpace: 'nowrap', flexShrink: 0, alignSelf: 'center',
                    fontSize: 13, fontWeight: 600, color: '#A5432B',
                    border: '1px solid #A5432B', borderRadius: 8,
                    padding: '6px 14px', textDecoration: 'none',
                    backgroundColor: 'transparent',
                }}>
                    Prejsť na vyšší plán
                </Link>
            )}
        </div>
    );
};

const getPageRange = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    if (current <= 4) return [1, 2, 3, 4, 5, '…', total];
    if (current >= total - 3) return [1, '…', total - 4, total - 3, total - 2, total - 1, total];
    return [1, '…', current - 1, current, current + 1, '…', total];
};

const pageBtnStyle = (active, disabled) => ({
    border: 'none', borderRadius: 8, padding: '4px 10px',
    fontSize: 13, fontWeight: 600,
    cursor: disabled ? 'default' : 'pointer',
    minWidth: 32,
    backgroundColor: active ? '#0B1157' : '#F1F2F6',
    color: active ? '#4DFFFF' : disabled ? '#C8CBDA' : '#5B6178',
});

const isTabCategoryComplete = (unit, key) => {
    if (key === 'CONFORMITY_ASSESSMENT') return !!unit.conformitySummary?.complete;
    if (key === 'TECHNICAL_DOCS') return !!unit.technicalDocumentationStatus?.current;
    if (key === 'EU_DECLARATION') return !!unit.euDeclarationStatus?.current;
    if (key === 'SUPPLIER_DOCS') return !!unit.supplierDocsConfirmedAt;
    if (key === 'DISTRIBUTOR_VERIFICATION') return !!unit.distributorVerificationSummary?.complete;
    if (key === 'PRODUCER_VERIFICATION') return !!unit.producerVerificationSummary?.complete;
    return false;
};

const PPWR = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const units = useSelector(state => state.packagingUnit.data);
    const loading = useSelector(state => state.packagingUnit.loading);
    const tenant = useSelector(state => state.tenant.current);

    const ppwrPlan = tenant?.ppwrPlan ?? null;
    const ppwrLimits = ppwrPlan ? PPWR_PLAN_LIMITS[ppwrPlan] : null;
    const hasUnitLimit = ppwrLimits?.maxPackagingUnits < 9999;

    const [modalStep, setModalStep] = useState(null);
    const [pendingRoles, setPendingRoles] = useState(null);
    const [unitToDelete, setUnitToDelete] = useState(null);
    const [detailUnitId, setDetailUnitId] = useState(null);
    const [editUnit, setEditUnit] = useState(null);
    const [copyUnit, setCopyUnit] = useState(null);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteError, setDeleteError] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [page, setPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterCharacter, setFilterCharacter] = useState('');
    const [filterRole, setFilterRole] = useState('');
    const [showArchived, setShowArchived] = useState(false);
    const [unitToArchive, setUnitToArchive] = useState(null);

    useEffect(() => {
        dispatch(fetchPackagingUnits());
    }, [dispatch]);

    useEffect(() => { setPage(1); }, [units.length]);

    const filteredUnits = units.filter((unit) => {
        if (showArchived ? !unit.archivedAt : !!unit.archivedAt) return false;
        if (searchQuery.trim() && !unit.name?.toLowerCase().includes(searchQuery.trim().toLowerCase())) return false;
        if (filterType && unit.packagingType !== filterType) return false;
        if (filterCharacter && unit.character !== filterCharacter) return false;
        if (filterRole && !matchesRoleFilter(unit, filterRole)) return false;
        return true;
    });

    const hasActiveFilters = !!(searchQuery.trim() || filterType || filterCharacter || filterRole);
    const clearFilters = () => {
        setSearchQuery('');
        setFilterType('');
        setFilterCharacter('');
        setFilterRole('');
    };

    useEffect(() => { setPage(1); }, [searchQuery, filterType, filterCharacter, filterRole, showArchived]);

    const handleDelete = () => {
        if (!unitToDelete) return;
        setDeleting(true);
        setDeleteError('');
        dispatch(deletePackagingUnit({ packagingUnitId: unitToDelete._id, password: deletePassword }))
            .unwrap()
            .then(() => {
                dispatch(fetchPackagingUnits());
                setUnitToDelete(null);
                setDeletePassword('');
            })
            .catch((err) => {
                if (err?.message?.includes('403')) {
                    setDeleteError('Nesprávne heslo. Skúste znova.');
                }
            })
            .finally(() => setDeleting(false));
    };

    const handleDeleteModalClose = () => {
        setUnitToDelete(null);
        setDeletePassword('');
        setDeleteError('');
    };

    const handleWizardComplete = (roles) => {
        setPendingRoles(roles);
        setModalStep('form');
    };

    const handleBackToWizard = () => setModalStep('wizard');
    const handleModalClose = () => { setModalStep(null); setPendingRoles(null); setCopyUnit(null); };

    const handleArchive = () => {
        if (!unitToArchive) return;
        dispatch(setPackagingUnitArchived({ packagingUnitId: unitToArchive._id, archived: true }))
            .unwrap()
            .finally(() => setUnitToArchive(null));
    };

    const handleUnarchive = (unit) => {
        dispatch(setPackagingUnitArchived({ packagingUnitId: unit._id, archived: false }));
    };

    const handleCopyUnit = (unit) => {
        setCopyUnit(unit);
        setPendingRoles({ baseRole: unit.baseRole, producerRole: unit.producerRole });
        setModalStep('form');
    };

    const copyDefaultValues = copyUnit ? {
        name: `${copyUnit.name} (kópia)`,
        code: copyUnit.code || '',
        gtin: copyUnit.gtin || '',
        packagingType: copyUnit.packagingType || '',
        character: copyUnit.character || '',
        foodContact: !!copyUnit.foodContact,
        recyclable: !!copyUnit.recyclable,
        deposit: !!copyUnit.deposit,
        usage: copyUnit.usage || '',
        digitalPassportUrl: copyUnit.digitalPassportUrl || '',
        description: copyUnit.description || '',
        internalNote: copyUnit.internalNote || '',
        marketCountries: copyUnit.marketCountries || [],
        marketDate: copyUnit.marketDate ? new Date(copyUnit.marketDate) : '',
        baseRole: copyUnit.baseRole || '',
        producerRole: copyUnit.producerRole || 'NOT_APPLICABLE',
    } : null;

    const totalPages = Math.ceil(filteredUnits.length / PAGE_SIZE);
    const safePage = Math.min(page, totalPages || 1);
    const pagedUnits = filteredUnits.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
    const pageRange = getPageRange(safePage, totalPages);

    const thStyle = { padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#5B6178', textAlign: 'left', whiteSpace: 'nowrap' };
    const tdStyle = { padding: '10px 12px', verticalAlign: 'middle', fontSize: 13, color: '#0B1157' };

    if (!ppwrPlan) {
        return <ModuleGate />;
    }

    const activeUnitsCount = units.filter(u => !u.archivedAt).length;
    const archivedUnitsCount = units.filter(u => !!u.archivedAt).length;
    const isAtUnitLimit = hasUnitLimit && units.length >= ppwrLimits.maxPackagingUnits;

    return (
        <div>
            {hasUnitLimit && (isAtUnitLimit || ppwrLimits.maxPackagingUnits - units.length <= 3) && (
                <UnitLimitBanner current={units.length} max={ppwrLimits.maxPackagingUnits} />
            )}
            <div className="d-flex justify-content-between align-items-center mb-4 gap-2">
                {showArchived ? (
                    <OverlayTrigger placement="top" overlay={<Tooltip>Späť</Tooltip>}>
                        <button
                            onClick={() => setShowArchived(false)}
                            style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                        >
                            <BtnBack className="btnBack" alt="Späť" />
                        </button>
                    </OverlayTrigger>
                ) : (
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="d-flex align-items-center gap-1"
                        onClick={() => setShowArchived(true)}
                    >
                        <IoArchiveOutline size={15} />
                        {`Archív${archivedUnitsCount > 0 ? ` (${archivedUnitsCount})` : ''}`}
                    </Button>
                )}
                {!showArchived && (
                    <Button className="button-custom-large radius" onClick={() => setModalStep('wizard')} disabled={isAtUnitLimit}>
                        Pridať obalovú jednotku
                    </Button>
                )}
            </div>

            {loading ? (
                <Loader />
            ) : !showArchived && units.filter(u => !u.archivedAt).length === 0 ? (
                <div className="text-center text-muted py-5">
                    <p>Zatiaľ nemáte žiadne obalové jednotky.</p>
                </div>
            ) : showArchived && units.filter(u => !!u.archivedAt).length === 0 ? (
                <div className="text-center text-muted py-5">
                    <IoArchiveOutline size={40} style={{ color: '#c3c7dc', marginBottom: 12 }} />
                    <p>Archív je prázdny.</p>
                </div>
            ) : (
                <>
                    <div className="row g-4 align-items-stretch mb-4">
                        <div className="col-12 col-xl-6">
                            <DocumentationStatusChart units={units} />
                        </div>
                        <div className="col-12 col-xl-6">
                            <RoleDistributionChart units={units} />
                        </div>
                    </div>

                    <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
                        <Form.Group style={{ minWidth: 220, flex: '1 1 220px' }}>
                            <Form.Label style={{ fontSize: 11, fontWeight: 700, color: '#5B6178', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Vyhľadať podľa názvu
                            </Form.Label>
                            <Form.Control
                                type="text"
                                size="sm"
                                placeholder="Zadajte názov obalovej jednotky..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group style={{ minWidth: 170, flex: '1 1 170px' }}>
                            <Form.Label style={{ fontSize: 11, fontWeight: 700, color: '#5B6178', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Typ obalu
                            </Form.Label>
                            <Form.Select size="sm" value={filterType} onChange={e => setFilterType(e.target.value)}>
                                <option value="">Všetky typy</option>
                                {PACKAGING_TYPE_OPTIONS.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group style={{ minWidth: 170, flex: '1 1 170px' }}>
                            <Form.Label style={{ fontSize: 11, fontWeight: 700, color: '#5B6178', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Charakter
                            </Form.Label>
                            <Form.Select size="sm" value={filterCharacter} onChange={e => setFilterCharacter(e.target.value)}>
                                <option value="">Všetky</option>
                                {PACKAGING_CHARACTER_OPTIONS.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group style={{ minWidth: 170, flex: '1 1 170px' }}>
                            <Form.Label style={{ fontSize: 11, fontWeight: 700, color: '#5B6178', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Postavenie
                            </Form.Label>
                            <Form.Select size="sm" value={filterRole} onChange={e => setFilterRole(e.target.value)}>
                                <option value="">Všetky</option>
                                {ROLE_FILTER_OPTIONS.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        {hasActiveFilters && (
                            <Button variant="link" className="text-muted mb-1" size="sm" onClick={clearFilters}>
                                Zrušiť filtre
                            </Button>
                        )}
                    </div>

                    {filteredUnits.length === 0 ? (
                        <div className="text-center text-muted py-5">
                            <p>Žiadna obalová jednotka nezodpovedá zvoleným filtrom.</p>
                        </div>
                    ) : (
                    <div style={{ borderRadius: 14, overflowX: 'auto', border: '1px solid #EEF0F8' }}>
                        <table style={{ width: '100%', minWidth: 900, borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#E4E6F3' }}>
                                    <th style={thStyle}>Názov</th>
                                    <th style={thStyle}>Kód / EAN</th>
                                    <th style={thStyle}>GTIN</th>
                                    <th style={thStyle}>Typ obalu</th>
                                    <th style={thStyle}>Charakter</th>
                                    <th style={thStyle}>Uvedenie na trh</th>
                                    <th style={thStyle}>Postavenie</th>
                                    <th style={thStyle}>Dokumentácia</th>
                                    <th style={thStyle}>Interná poznámka</th>
                                    <th style={{ ...thStyle, width: 100 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagedUnits.map((unit, i) => {
                                    const requiredCategoryKeys = getRequiredDocumentCategoryKeys(unit.baseRole, unit.producerRole);
                                    const completedCount = requiredCategoryKeys.filter(k => isTabCategoryComplete(unit, k)).length;
                                    const isAllComplete = completedCount === requiredCategoryKeys.length;
                                    return (
                                        <tr
                                            key={unit._id}
                                            onMouseEnter={() => setHoveredRow(unit._id)}
                                            onMouseLeave={() => setHoveredRow(null)}
                                            style={{
                                                background: hoveredRow === unit._id ? '#EEF1FB' : ((safePage - 1) * PAGE_SIZE + i) % 2 === 0 ? '#fff' : '#F9FAFF',
                                                borderTop: '1px solid #EEF0F8',
                                                cursor: 'default',
                                            }}
                                        >
                                            <td style={{ ...tdStyle, fontWeight: 600 }}>
                                                {unit.name}
                                                {unit.archivedAt && (
                                                    <span className="badge bg-secondary ms-2" style={{ fontSize: 10, fontWeight: 600, verticalAlign: 'middle' }}>
                                                        Archivované
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ ...tdStyle, color: unit.code ? '#0B1157' : '#9AA0B4' }}>{unit.code || '—'}</td>
                                            <td style={{ ...tdStyle, color: unit.gtin ? '#0B1157' : '#9AA0B4' }}>{unit.gtin || '—'}</td>
                                            <td style={tdStyle}>{PACKAGING_TYPE_LABELS[unit.packagingType] ?? unit.packagingType}</td>
                                            <td style={tdStyle}>{PACKAGING_CHARACTER_LABELS[unit.character] ?? unit.character}</td>
                                            <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                                                {unit.marketDate ? new Date(unit.marketDate).toLocaleDateString('sk-SK') : <span style={{ color: '#9AA0B4' }}>—</span>}
                                            </td>
                                            <td style={tdStyle}>
                                                {unit.baseRole ? (
                                                    <div className="d-flex flex-wrap gap-1">
                                                        {getRoleBadges(unit.baseRole, unit.producerRole).map((badge, bi) => (
                                                            <span key={bi} className={`badge bg-${badge.variant}`}>{badge.label}</span>
                                                        ))}
                                                    </div>
                                                ) : <span style={{ color: '#9AA0B4' }}>—</span>}
                                            </td>
                                            <td style={tdStyle}>
                                                <span
                                                    className="d-inline-flex align-items-center gap-1"
                                                    style={{
                                                        fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 999,
                                                        backgroundColor: isAllComplete ? '#E6F4EA' : completedCount > 0 ? '#FCEFE3' : '#F1F2F6',
                                                        color: isAllComplete ? '#198754' : completedCount > 0 ? '#C97D2C' : '#9AA0B4',
                                                    }}
                                                >
                                                    {isAllComplete
                                                        ? <IoCheckmarkCircle size={12} />
                                                        : <IoAlertCircleOutline size={12} />}
                                                    {completedCount}/{requiredCategoryKeys.length}
                                                </span>
                                            </td>
                                            <td
                                                style={{
                                                    ...tdStyle,
                                                    color: unit.internalNote ? '#0B1157' : '#9AA0B4',
                                                    maxWidth: 220,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                                title={unit.internalNote || undefined}
                                            >
                                                {unit.internalNote || '—'}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                                                <div className="d-flex gap-1 justify-content-end">
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Otvoriť detail</Tooltip>}>
                                                        <Button className="btnIcon" onClick={() => setDetailUnitId(unit._id)}>
                                                            <DocumentIcon className="icon icon-document" alt="Otvoriť" />
                                                        </Button>
                                                    </OverlayTrigger>
                                                    {!unit.archivedAt && (
                                                        <>
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Upraviť</Tooltip>}>
                                                                <Button className="btnIcon" onClick={() => setEditUnit(unit)}>
                                                                    <EditIcon className="icon icon-edit" alt="Upraviť" />
                                                                </Button>
                                                            </OverlayTrigger>
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Kopírovať</Tooltip>}>
                                                                <Button className="btnIcon" onClick={() => handleCopyUnit(unit)}>
                                                                    <IoCopyOutline className="icon" size={16} style={{ color: '#718094' }} />
                                                                </Button>
                                                            </OverlayTrigger>
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Vymazať</Tooltip>}>
                                                                <Button className="btnIcon" onClick={() => setUnitToDelete(unit)}>
                                                                    <RemoveIcon className="icon icon-remove" alt="Vymazať" />
                                                                </Button>
                                                            </OverlayTrigger>
                                                        </>
                                                    )}
                                                    {unit.archivedAt && (
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Obnoviť z archívu</Tooltip>}>
                                                            <Button className="btnIcon" onClick={() => handleUnarchive(unit)}>
                                                                <IoArrowUndoOutline size={16} style={{ color: '#198754' }} />
                                                            </Button>
                                                        </OverlayTrigger>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    )}

                    {totalPages > 1 && (
                        <div className="d-flex align-items-center justify-content-between mt-2 flex-wrap gap-2">
                            <span style={{ fontSize: 12, color: '#9AA0B4' }}>
                                {filteredUnits.length} jednotiek · strana {safePage} z {totalPages}
                            </span>
                            <div className="d-flex gap-1">
                                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1} style={pageBtnStyle(false, safePage === 1)}>‹</button>
                                {pageRange.map((p, i) =>
                                    p === '…'
                                        ? <span key={`e-${i}`} style={{ padding: '4px 6px', fontSize: 13, color: '#9AA0B4', alignSelf: 'center' }}>…</span>
                                        : <button key={p} onClick={() => setPage(p)} style={pageBtnStyle(safePage === p, false)}>{p}</button>
                                )}
                                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} style={pageBtnStyle(false, safePage === totalPages)}>›</button>
                            </div>
                        </div>
                    )}
                </>
            )}

            <CustomModal isVisible={modalStep !== null} onClose={handleModalClose}>
                {modalStep === 'wizard' && (
                    <RoleWizard onComplete={handleWizardComplete} onClose={handleModalClose} />
                )}
                {modalStep === 'form' && (
                    <AddPackagingUnit
                        baseRole={pendingRoles?.baseRole}
                        producerRole={pendingRoles?.producerRole}
                        onClose={handleModalClose}
                        onBack={copyUnit ? null : handleBackToWizard}
                        defaultValues={copyDefaultValues}
                        cloneOf={copyUnit?.name}
                    />
                )}
            </CustomModal>

            <CustomModal isVisible={!!editUnit} onClose={() => setEditUnit(null)}>
                {editUnit && (
                    <EditPackagingUnit unit={editUnit} onClose={() => setEditUnit(null)} />
                )}
            </CustomModal>

            <CustomModalFullscreen isVisible={!!detailUnitId} onClose={() => setDetailUnitId(null)}>
                {detailUnitId && (
                    <PackagingUnitDetail unitId={detailUnitId} />
                )}
            </CustomModalFullscreen>


            <Modal show={!!unitToArchive} onHide={() => setUnitToArchive(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Archivovať obalovú jednotku</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-0">
                        Naozaj chcete archivovať <strong>{unitToArchive?.name}</strong>?
                        Archivovanú jednotku nie je možné vymazať — môžete ju kedykoľvek obnoviť z archívu.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setUnitToArchive(null)}>Zrušiť</Button>
                    <Button variant="warning" onClick={handleArchive}>Archivovať</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={!!unitToDelete} onHide={handleDeleteModalClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Vymazať obalovú jednotku</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-3">
                        Naozaj chcete vymazať obalovú jednotku <strong>{unitToDelete?.name}</strong>?
                        Táto akcia je nevratná — vymažú sa aj všetky súbory a dokumenty.
                    </p>
                    <Form.Group>
                        <Form.Label style={{ fontWeight: 600, fontSize: 13 }}>Potvrďte heslom</Form.Label>
                        <Form.Control
                            type="password"
                            placeholder="Vaše heslo"
                            value={deletePassword}
                            onChange={e => { setDeletePassword(e.target.value); setDeleteError(''); }}
                            onKeyDown={e => e.key === 'Enter' && deletePassword && handleDelete()}
                            isInvalid={!!deleteError}
                            autoComplete="current-password"
                        />
                        {deleteError && (
                            <Form.Control.Feedback type="invalid">{deleteError}</Form.Control.Feedback>
                        )}
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleDeleteModalClose}>Zrušiť</Button>
                    <Button variant="danger" disabled={!deletePassword || deleting} onClick={handleDelete}>
                        {deleting ? 'Vymazáva sa...' : 'Vymazať'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PPWR;
